import React from 'react'

import { ZariContext } from '_zari/context'
import { Modal, Button, Table, Row, Col, Form } from '_zari/core'

import { TiposTablaPersonalizadaConstant } from 'constants/index'

import * as RequerimientoService from 'services/requerimiento.service'
import * as TablasPersonalizadasService from 'services/tablasPersonalizadas.service'

const InfoItem = ({ label, value }) => (
  <Row className='mb-2'>
    <Col md={5}>
      <strong style={{ color: '#5867dd', fontSize: '0.85rem' }}>{label}</strong>
    </Col>
    <Col md={7}>
      <span>{value || '--'}</span>
    </Col>
  </Row>
)

const VerDetalleModal = ({ show, handleClose, idRequerimiento }) => {
  const __zariContext = React.useContext(ZariContext)

  const [loading, setLoading] = React.useState(false)
  const [data, setData] = React.useState(null)
  const [nombreOficina, setNombreOficina] = React.useState('')
  const [nombreUsuario, setNombreUsuario] = React.useState('')

  React.useEffect(() => {
    if (!show || !idRequerimiento) return

    setLoading(true)

    Promise.all([
      RequerimientoService.obtenerxIdAsEdicion(idRequerimiento),
      TablasPersonalizadasService.getAsSelect(TiposTablaPersonalizadaConstant.OFICINA)
    ])
      .then(([resReq, resOficinas]) => {
        const req = resReq.data
        setData(req)
        const oficina = resOficinas.data.find(o => o._id === req._idOficinaSolicitante)
        setNombreOficina(oficina ? oficina.nombre : '--')
        setNombreUsuario(req.nombreUsuario || '--')
      })
      .catch(({ data: { error } }) => __zariContext.snack('error', error))
      .finally(() => setLoading(false))
  }, [show, idRequerimiento, __zariContext])

  return (
    <Modal show={show} size='lg'>
      <Modal.Header>
        <Modal.Title>Detalle del Requerimiento</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading || !data ? (
          <div>Cargando...</div>
        ) : (
          <>
            <Row className='mb-3'>
              <Col md={6}>
                <InfoItem label='FEC. EMISIÓN' value={data.fechaEmision} />
                <InfoItem label='GLOSA' value={data.glosa} />
                <InfoItem label='CORRELATIVO' value={data.correlativo} />
              </Col>
              <Col md={6}>
                <InfoItem label='OFICINA SOLICITANTE' value={nombreOficina} />
                <InfoItem label='USUARIO SOLICITANTE' value={nombreUsuario} />
                <InfoItem label='FEC. NECESIDAD' value={data.fechaNecesidad} />
                <InfoItem label='PRIORIDAD' value={data.prioridad} />
              </Col>
            </Row>
            <Table
              headRows={[
                { label: 'CÓDIGO' },
                { label: 'NOMBRE' },
                { label: 'U. MED' },
                { label: 'CANTIDAD' },
                { label: 'STOCK' },
                { label: 'ESTADO' },
                { label: 'OBS.' }
              ]}
              filas={(data.detalles || []).flatMap(row => {
                const esMixto = row.flagAlmacen && row.flagOrdenCompra
                if (!esMixto) return [{ ...row, __destino: row.flagAlmacen ? 'almacen' : row.flagOrdenCompra ? 'oc' : 'none' }]
                const cA = row.cantidadAlmacen || 0
                const cOC = row.cantidadOrdenCompra || (row.cantidad - cA)
                return [
                  { ...row, __destino: 'almacen', __cantidadMostrar: cA, __split: true },
                  { ...row, __destino: 'oc', __cantidadMostrar: cOC, __split: true }
                ]
              })}
              rowTemplate={(row, k) => {
                let destinoLabel = '--'
                let destinoColor = '#6c757d'
                // Sub-cantidades atendidas por destino — usadas para decidir si la
                // porción correspondiente ya fue entregada.
                const cA = row.cantidadAlmacen || 0
                const cOC = row.cantidadOrdenCompra || 0
                const atendidaAlmacen = row.cantidadAtendidaAlmacen || 0
                const atendidaOC = row.cantidadAtendidaOrdenCompra || 0
                const asignadoAlmacen = cA > 0 ? cA : (row.flagAlmacen ? row.cantidad : 0)
                const asignadoOC = cOC > 0 ? cOC : (row.flagOrdenCompra ? row.cantidad : 0)

                if (row.__destino === 'almacen') {
                  const entregadoAlmacen = asignadoAlmacen > 0 && atendidaAlmacen >= asignadoAlmacen
                  destinoLabel = entregadoAlmacen ? 'Entregado' : 'Almacén'
                  destinoColor = entregadoAlmacen ? '#155724' : '#28a745'
                } else if (row.__destino === 'oc') {
                  const entregadoOC = asignadoOC > 0 && atendidaOC >= asignadoOC
                  destinoLabel = entregadoOC ? 'Entregado' : 'Orden de Compra'
                  destinoColor = entregadoOC ? '#155724' : '#007bff'
                }
                const cantidadMostrar = row.__cantidadMostrar != null ? row.__cantidadMostrar : row.cantidad
                return (
                  <Table.Row key={`${row._id || row._idProducto}-${row.__destino}-${k}`}>
                    <Table.Cell>{row.codigoProducto ?? '--'}</Table.Cell>
                    <Table.Cell>{row.nombreProducto ?? '--'}</Table.Cell>
                    <Table.Cell>{row.unidadMedida ?? '--'}</Table.Cell>
                    <Table.Cell>{cantidadMostrar ?? '--'}</Table.Cell>
                    <Table.Cell>
                      <span className={(row.stockDisponible || 0) >= cantidadMostrar ? 'text-success' : 'text-danger'}>
                        {row.stockDisponible ?? 0}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span
                        style={{
                          backgroundColor: destinoColor,
                          color: '#fff',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {destinoLabel}
                      </span>
                      {row.cantidadAtendida > 0 && !row.__split && (
                        <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '2px' }}>
                          Atend: {row.cantidadAtendida}
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell>{row.observacion ?? '--'}</Table.Cell>
                  </Table.Row>
                )
              }}
            />
            <Row className='mt-3'>
              <Col md={12}>
                <strong style={{ color: '#5867dd', fontSize: '0.85rem' }}>OBSERVACIONES</strong>
                <p className='mt-1' style={{ whiteSpace: 'pre-wrap' }}>{data.observacionesGenerales || '--'}</p>
              </Col>
            </Row>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button outline color='secondary' onClick={handleClose}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default React.memo(VerDetalleModal)
